import { create } from "zustand";
import type { UnknownRecord } from "../types/common";

export interface AmendmentReplayProgress {
  request_id: string;
  current_book_id: string;
  phase: string;
  books_done: number;
  books_total: number;
}

export interface AmendmentReplayCompleted {
  request_id: string;
  replay_book_set: Array<{ entity_id: string; book_id: string }>;
  book_ids: string[];
  trades_affected: number;
  cashflows_affected: number;
  duration_ms: number;
}

interface AmendmentReplayState {
  progress: AmendmentReplayProgress | null;
  completed: AmendmentReplayCompleted | null;
  applyProgress: (data: UnknownRecord) => void;
  applyCompleted: (data: UnknownRecord) => void;
  clear: () => void;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number {
  return typeof v === "number" ? v : Number(v) || 0;
}

export const useAmendmentReplayStore = create<AmendmentReplayState>((set) => ({
  progress: null,
  completed: null,
  applyProgress: (data) => {
    set({
      progress: {
        request_id: str(data.request_id),
        current_book_id: str(data.current_book_id),
        phase: str(data.phase),
        books_done: num(data.books_done),
        books_total: num(data.books_total),
      },
    });
  },
  applyCompleted: (data) => {
    const replaySetRaw = Array.isArray(data.replay_book_set) ? data.replay_book_set : [];
    const replay_book_set = replaySetRaw
      .filter((x): x is UnknownRecord => x != null && typeof x === "object")
      .map((x) => ({
        entity_id: str(x.entity_id),
        book_id: str(x.book_id),
      }))
      .filter((x) => x.book_id);

    const bookIdsRaw = Array.isArray(data.book_ids) ? data.book_ids : [];
    const book_ids = bookIdsRaw.map((x) => String(x)).filter(Boolean);

    set({
      completed: {
        request_id: str(data.request_id),
        replay_book_set,
        book_ids: book_ids.length ? book_ids : replay_book_set.map((b) => b.book_id),
        trades_affected: num(data.trades_affected),
        cashflows_affected: num(data.cashflows_affected),
        duration_ms: num(data.duration_ms),
      },
      progress: null,
    });
  },
  clear: () => set({ progress: null, completed: null }),
}));
